import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';

/**
 * The prototype's `.btn` (Story #27999, DL-LF-02). Variants:
 *  - `default` / `secondary` - the outlined button the prototype draws for every ordinary action
 *  - `primary` - navy fill (`.btn.pri`); still the default variant so existing callers keep their look
 *  - `ghost` - no border (`.btn.ghost`)
 *  - `danger` - red OUTLINE (`.btn.danger`), not a red fill
 *  - `link` - an action drawn as the prototype's text link (`.lnk`): navy, bold, no box (Story #28000)
 * Sizes: `md` (34px), `sm` (30px), `xs` (24px); `lg` is kept for existing callers and renders as `md`.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'ghost' | 'danger' | 'link' | 'text';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: ReactNode;
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  default: '',
  secondary: '',
  primary: 'ui-pri',
  ghost: 'ui-ghost',
  danger: 'ui-danger',
  link: 'ui-link',
  text: 'ui-text',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  xs: 'ui-xs',
  sm: 'ui-sm',
  md: '',
  lg: '',
};

/**
 * Forwards its ref to the `<button>`, so a dialog can open with focus on its primary
 * action (`Modal`'s `initialFocusRef`).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, children, className = '', ...props },
  ref,
) {
  const classes = ['ui-btn', variantClasses[variant], sizeClasses[size], className].filter(Boolean).join(' ');
  return (
    <button ref={ref} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {loading ? (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : null}
      {children}
    </button>
  );
});
